/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, DismissIconButton, Image, Text } from '@/shared/ui';
import { getInitials, formatLastAccess } from './use-workspace-helpers';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';
import type { AgencyStatus } from '@/features/onboarding/types/agency-dashboard';
import type { AgencyDashboardItem } from './ManageAgenciesCards';

type WorkspaceCardProps = {
  agency: AgencyDashboardItem;
  onClick: () => void;
  /**
   * Deletes this draft. Only ever passed for a `no_subscription` agency --
   * everything else (active, cancelled, payment_failed...) was once real or
   * still is, and the backend's own `abandon_agency` refuses it anyway.
   */
  onDelete?: () => void;
};

/**
 * Selectable workspace tile for a single agency: shows its logo or initials
 * avatar, name, subscription-status badge, location, and relative last-access
 * time, invoking the click handler to enter that agency's workspace.
 *
 * @param props.agency - The agency dashboard item to render.
 * @param props.onClick - Handler invoked when the card is activated.
 * @param props.onDelete - Handler invoked by the delete (X) control, when shown.
 * @returns The agency workspace card.
 */
export function WorkspaceCard({ agency, onClick, onDelete }: WorkspaceCardProps) {
  const { t } = useTranslation('@pages/agencies');
  const lastAccess = formatLastAccess(agency.last_access, t);
  return (
    // A `<div>` around two sibling buttons, not a button nested inside a
    // button: the delete control used to live inside the card's own Button,
    // which is invalid HTML and made the X unreachable by itself -- clicking
    // it always activated the card underneath it too.
    <div
      data-testid="workspace-card"
      className="relative rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-card)"
    >
      <Button
        variant="none"
        disableSize
        data-testid="workspace-card-button"
        onClick={onClick}
        // Top-aligned, not centred: the name wraps to two lines whenever it
        // is long, and centring floated the avatar down the card, so two
        // tiles side by side had their squares at different heights.
        className="flex items-start gap-(--space-3) md:gap-(--space-4) p-(--space-5) text-left cursor-pointer transition-colors w-full"
      >
        {agency.image ? (
          <Image
            src={agency.image}
            alt={agency.name}
            data-testid="workspace-avatar"
            className="w-11 h-11 md:w-12 md:h-12 rounded-(--radius-lg) shrink-0 object-cover"
          />
        ) : (
          <div
            data-testid="workspace-avatar"
            className="w-11 h-11 md:w-12 md:h-12 rounded-(--radius-lg) flex items-center justify-center shrink-0 text-base font-bold bg-(--seed-base) text-(--text-inverse)"
          >
            {getInitials(agency.name)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-(--space-2) min-w-0">
            {/* The name is never cut: an agency has to recognise its own
                workspace here, and "Demo Imobiliária Suspensa (Payien..." is
                not a name. It wraps to as many lines as it needs, which is
                why the row above aligns to the top rather than the centre.
                `min-w-0` keeps a single long word wrapping inside the card
                instead of pushing the badge past its edge. */}
            <Text className="min-w-0 text-base md:text-lg font-semibold m-0 text-(--text-primary)">
              {agency.name}
            </Text>
            <Text as="span" className="shrink-0">
              <SubscriptionStatusBadge status={agency.status as AgencyStatus} />
            </Text>
          </div>
          <Text className="text-sm text-(--text-tertiary) m-0 mt-(--space-1) truncate">
            {agency.city}{agency.state ? `, ${agency.state}` : ''}
          </Text>
          {lastAccess && (
            <Text as="span" data-testid="workspace-last-access" className="text-xs text-(--text-tertiary) mt-(--space-0-5) block">
              {lastAccess}
            </Text>
          )}
        </div>
      </Button>
      {onDelete && (
        <DismissIconButton
          label={t('workspace.deleteDraft.ariaLabel')}
          className="absolute top-(--space-2) right-(--space-2)"
          onClick={onDelete}
        />
      )}
    </div>
  );
}
